import type { ScriptBlockData } from '../components/story/ScriptBlock';
import type { BlockType } from '../components/story/SlashMenu';
import type { TitlePageData } from '../components/story/TitlePage';
import {dualFor, type DualParagraph} from './scriptDualDialogue';
import {EMPTY_SCRIPT_FORMAT,resolveParagraphFormat,resolveScriptPage,scriptSentenceBreaksEnabled,usesSentenceBreaks,scriptElementFont,type ScriptFormatSource} from './scriptFormat';
import {firstScriptSentenceBoundary,lastScriptSentenceBoundary} from './scriptSentenceBreak';
import {runsFor,scriptFontSize} from './scriptRichText';

/** One physical layout contract for preview, statistics and PDF. Courier's
 * supported glyphs have a fixed 600/1000-em advance: 7.2pt at 12pt. */
export const SCRIPT_PAGE = {width:612, height:792, top:72, bottom:72,
    fontSize:12, lineHeight:12, glyphWidth:7.2, rows:54} as const;
export interface WrappedLine { text:string; sourceStart:number; sourceEnd:number }
export interface PrintLine extends WrappedLine {
    blockId:string; field:string; type:BlockType; kind:'content'|'more'|'continued'|'page-number'|'title';
    x:number; y:number; width:number; align:'left'|'right'|'center'; lineHeight?:number;
    /** Resolved inherited size; explicit run Size overrides it. */
    fontSize?:number; ascent?:number;
}
export interface ScriptPage { pageNumber:number; lines:PrintLine[] }
export interface ScriptLayout { pages:ScriptPage[]; issues:string[]; warnings?:string[] }
export interface PrintPage { kind:'script'|'title'; pageNumber?:number; lines:PrintLine[] }
interface Style { x:number; width:number; before:number; align:PrintLine['align']; lineHeight?:number; fontSize?:number; ascent?:number }
export const SCRIPT_BODY = {left:108,right:72,width:432} as const;
export const SCRIPT_DUAL_COLUMNS = {width:198,gap:36} as const;
const styles:Record<Exclude<BlockType,'dual-dialogue'>,Style> = {
    'scene-heading':{x:108,width:432,before:2,align:'left'},
    action:{x:108,width:432,before:1,align:'left'},
    character:{x:252,width:270,before:1,align:'left'},
    dialogue:{x:180,width:252,before:0,align:'left'},
    parenthetical:{x:216,width:180,before:0,align:'left'},
    transition:{x:396,width:144,before:1,align:'right'},
    shot:{x:108,width:432,before:2,align:'left'},
};
/** Shared ezart geometry in points; exact native pagination remains separate. */
export function scriptElementGeometry(type:BlockType):Readonly<Style> {
    return type==='dual-dialogue'?{x:108,width:432,before:1,align:'left'}:styles[type];
}
export function scriptDualGeometry(side:'left'|'right',type:DualParagraph['type']):Readonly<Style> {
    const x=SCRIPT_BODY.left+(side==='right'?SCRIPT_DUAL_COLUMNS.width+SCRIPT_DUAL_COLUMNS.gap:0);
    return {x:x+(type==='character'?36:type==='parenthetical'?18:0),width:type==='dialogue'?198:162,before:0,align:'left'};
}

/** Each source range includes whitespace consumed at the wrap and an explicit
 * newline when present. Concatenating source slices therefore recovers the input. */
export function wrapScriptText(text:string, width:number, firstWidth=width,sizeAt:(offset:number)=>number=()=>12):WrappedLine[] {
    if (!Number.isFinite(width) || !Number.isFinite(firstWidth) || Math.min(width,firstWidth) < SCRIPT_PAGE.glyphWidth) throw new Error('Text column is narrower than one Courier glyph.');
    const result:WrappedLine[] = [];
    function segment(start:number, end:number, newlineEnd:number):void {
        let cursor = start;
        if (cursor === end) result.push({text:'',sourceStart:start,sourceEnd:newlineEnd});
        while (cursor < end) {
            const available=result.length?width:firstWidth;
            let fit = cursor, advance = 0, wordBreak = -1;
            while (fit < end) {
                const character = String.fromCodePoint(text.codePointAt(fit)!);
                const glyph=sizeAt(fit)*0.6;
                const nextAdvance=glyph*(character==='\t'?Math.min(4,Math.max(1,Math.floor((available+1e-6)/glyph))):1);
                if (advance + nextAdvance > available+1e-6) break;
                advance += nextAdvance;
                fit += character.length;
                // FD13.4 classifier 00dac9b0 / selector 00dd3ec0 distinguish
                // a break AFTER the configured hyphen from consumed whitespace.
                // Keep Story's width/fallback policy; this is not hyphen insertion.
                if (character === ' ' || character === '\t' || character === '-') wordBreak = fit;
            }
            if (fit === cursor) fit += String.fromCodePoint(text.codePointAt(cursor)!).length;
            let next = fit < end && wordBreak > cursor ? wordBreak : fit;
            if (fit < end && (text[fit] === ' ' || text[fit] === '\t')) {
                next = fit;
                while (next < end && (text[next] === ' ' || text[next] === '\t')) next++;
            }
            const raw=text.slice(cursor,next).replace(/[ \t]+$/,'');
            result.push({text:raw.replace(/\t/g,(_,offset)=>' '.repeat(Math.min(4,Math.max(1,Math.floor((available+1e-6)/(sizeAt(cursor+offset)*.6)))))),
                sourceStart:cursor,sourceEnd:next === end ? newlineEnd : next});
            cursor = next;
        }
    }
    let start = 0;
    for (const match of text.matchAll(/\r\n|\r|\n/g)) {
        segment(start,match.index!,match.index! + match[0].length);
        start = match.index! + match[0].length;
    }
    segment(start,text.length,text.length);
    return result;
}

interface DualSide { character:string; dialogue:string }
export function readDualDialogue(content:string):{left:DualSide;right:DualSide} {
    const data:unknown = JSON.parse(content);
    const record = (value:unknown):Record<string,unknown> => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid dual dialogue.');
        return value as Record<string,unknown>;
    };
    const root = record(data);
    if (Object.keys(root).some(key => !['left','right','LEFT','RIGHT'].includes(key)) ||
        ('left' in root && 'LEFT' in root) || ('right' in root && 'RIGHT' in root)) throw new Error('Invalid dual dialogue.');
    const side = (value:unknown):DualSide => {
        const object = record(value);
        if (Object.keys(object).some(key => !['character','dialogue','CHARACTER','DIALOGUE'].includes(key)) ||
            ('character' in object && 'CHARACTER' in object) || ('dialogue' in object && 'DIALOGUE' in object)) throw new Error('Invalid dual dialogue.');
        const character = object.character ?? object.CHARACTER;
        const dialogue = object.dialogue ?? object.DIALOGUE;
        if (typeof character !== 'string' || typeof dialogue !== 'string') throw new Error('Invalid dual dialogue.');
        return {character,dialogue};
    };
    return {left:side(root.left ?? root.LEFT),right:side(root.right ?? root.RIGHT)};
}

// Standard PDF Courier uses WinAnsi; reject unsupported text rather than writing
// corrupted glyphs. Preview keeps the source. Embedded Unicode fonts are future work.
export function hasUnsupportedPDFText(text:string):boolean {
    const extras = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
    return Array.from(text).some(character => {
        const code = character.codePointAt(0)!;
        return !(code === 9 || code === 10 || code === 13 || code >= 32 && code <= 126 ||
            code >= 160 && code <= 255 || extras.includes(character));
    });
}

export function layoutScreenplay(blocks:readonly ScriptBlockData[],format:ScriptFormatSource=EMPTY_SCRIPT_FORMAT):ScriptLayout {
    return layoutScreenplayFlow(blocks,format,0);
}
// Dual children use the same speech fitter with a first-page cursor. Their
// later pages resume at the ordinary top margin; source blocks are never edited.
function layoutScreenplayFlow(blocks:readonly ScriptBlockData[],format:ScriptFormatSource,initialRow:number):ScriptLayout {
    const pages:ScriptPage[] = [{pageNumber:1,lines:[]}];
    const issues:string[] = [];
    const pageFormat=resolveScriptPage(format),warnings=new Set([...format.warnings,...pageFormat.warnings]);
    const fontSize=(raw:string|undefined,fallback=12)=>{
        const size=scriptFontSize(raw,fallback);
        if(raw!==undefined&&raw!==''&&Number(raw)!==size)warnings.add('Imported font sizes outside 6–24 points use the inherited Courier size; supported fractional sizes use whole points.');
        return size;
    };
    const checkFont=(font:string|undefined)=>{
        if(font&&font!=='Courier')warnings.add('Imported font faces, including Courier Final Draft and Courier New, use Story’s Courier approximation. Native line breaks may differ; the original face is retained in FDX.');
    };
    const textMetrics=new Map(blocks.filter(b=>b.type!=='dual-dialogue').map(block=>{
        const defaults=scriptElementFont(block.type,format);checkFont(defaults.Font);
        const base=fontSize(defaults.Size),runs=block.richText?.runs?runsFor(block):[{text:block.content,attributes:{}}];let end=0;
        const spans=runs.map(run=>{checkFont(run.attributes.Font);end+=run.text.length;return {end,size:fontSize(run.attributes.Size,base)};});
        return [block.id,{base,spans}];
    }));
    const sizeAt=(block:ScriptBlockData,offset:number)=>{
        const metrics=textMetrics.get(block.id);if(!metrics)return 12;
        return metrics.spans.find(run=>offset<run.end)?.size??metrics.spans.at(-1)?.size??metrics.base;
    };
    const formats=new Map(blocks.map(block=>{
        const resolved=resolveParagraphFormat(block,format,scriptElementGeometry(block.type)??styles.action);
        const metrics=textMetrics.get(block.id),widest=Math.max(metrics?.base??12,...(metrics?.spans.map(s=>s.size)??[]))*.6;
        if(resolved.width-Math.max(0,resolved.firstIndent)<widest){
            Object.assign(resolved,scriptElementGeometry(block.type)??styles.action,{firstIndent:0});
            warnings.add('A paragraph column narrower than its font uses the Story element geometry.');
        }
        resolved.warnings.forEach(w=>warnings.add(w));return [block.id,resolved] as const;
    }));
    const styleFor=(block:ScriptBlockData)=>formats.get(block.id)!;
    const wrapped=(block:ScriptBlockData,start=0,end=block.content.length)=>{
        const style=styleFor(block);
        const lines=wrapScriptText(block.content.slice(start,end),style.width,style.width-(start===0?style.firstIndent:0),offset=>sizeAt(block,start+offset));
        // A truncated range ending in CR/LF must not invent an empty final line;
        // the next range owns it. Keep actual blank lines and the true final one.
        if(end<block.content.length && lines.length>1 && lines.at(-1)!.sourceStart===end-start)lines.pop();
        return lines.map((line,index)=>{
            const from=line.sourceStart+start,to=from+block.content.slice(from,line.sourceEnd+start).replace(/[ \t\r\n]+$/,'').length;
            // FD13.4 00dc9f90 combines metric components across runs. Within
            // our one Courier family, component maxima scale with max size.
            let maximum=sizeAt(block,from),offset=0;
            for(const span of textMetrics.get(block.id)?.spans??[]){if(span.end>from&&offset<to)maximum=Math.max(maximum,span.size);offset=span.end;}
            const sized={...style,fontSize:textMetrics.get(block.id)?.base??12,ascent:maximum*8/10,lineHeight:maximum*style.lineHeight/12};
            return {block,line:{...line,sourceStart:line.sourceStart+start,sourceEnd:line.sourceEnd+start},
                style:index===0&&start===0?{...sized,x:style.x+style.firstIndent,width:style.width-style.firstIndent}:sized};
        });
    };
    type LayoutItem=ReturnType<typeof wrapped>[number];
    // Budget in 12pt units, including half units for 1.5 spacing. Line count
    // and occupied height are distinct; source offsets never use this budget.
    const height=(style:Style)=>(style.lineHeight??SCRIPT_PAGE.lineHeight)/SCRIPT_PAGE.lineHeight;
    const extent=(items:LayoutItem[])=>items.reduce((sum,item)=>sum+height(item.style),0);
    const fit=(items:LayoutItem[],cursor:number,budget:number)=>{
        let count=0,used=0;
        while(cursor+count<items.length && used+height(items[cursor+count].style)<=budget+1e-7){used+=height(items[cursor+count].style);count++;}
        return count;
    };
    // FD13.4 00da8d10 applies paragraph+24c at paragraph starts. Source offsets
    // keep this event stable when sentenceTake rewrites the wrapped stream.
    const paragraphGap=(item:LayoutItem)=>item.line.sourceStart===0?styleFor(item.block).before:0;
    const speechExtent=(items:LayoutItem[],omitted:ReadonlySet<string>=new Set())=>items.reduce((sum,item)=>sum+height(item.style)+(omitted.has(item.block.id)?0:paragraphGap(item)),0);
    function openingGapOmissions(cue:ScriptBlockData,body:ScriptBlockData[]):Set<string> {
        const omitted=new Set<string>();let used=extent(wrapped(cue));
        for(const block of body){
            const items=wrapped(block),gap=styleFor(block).before;
            if(gap>0 && used+gap+height(items[0].style)>pageFormat.rows)omitted.add(block.id);
            else used+=gap;
            if(block.type==='dialogue')break;
            used+=extent(items);
        }
        return omitted;
    }
    function speechBoundaries(body:ScriptBlockData[]) {
        const first=body.findIndex(block=>block.type==='dialogue');
        const openingBreak=body.slice(0,first+1).some(block=>styleFor(block).startsNewPage);
        const breaks=new Set<string>();
        body.forEach((block,index)=>{
            if(index<=first || !styleFor(block).startsNewPage)return;
            // Keep an introducing parenthetical with its following dialogue.
            // This grouping is Story policy; native continuation placement is
            // not established by the generic StartsNewPage predicate alone.
            while(index>first+1 && body[index-1].type==='parenthetical')index--;
            breaks.add(body[index].id);
        });
        return {first,openingBreak,breaks};
    }
    function openingRows(block:ScriptBlockData,budget:number):number {
        const lines=wrapped(block),minimum=extent(lines.slice(0,2));
        if(!scriptSentenceBreaksEnabled(format) || !usesSentenceBreaks(block.type,format))return minimum;
        const first=extent(wrapped(block,0,firstScriptSentenceBoundary(block.content)));
        // Adapt native00924090's paragraph-start candidate to Story's row engine.
        // An oversized opening must split rather than chase an impossible fit.
        return first<=budget?Math.max(minimum,first):minimum;
    }
    function speechKeep(cue:ScriptBlockData,body:ScriptBlockData[],budget:number):number {
        const {first,breaks}=speechBoundaries(body);
        const boundaryIndex=body.findIndex(block=>breaks.has(block.id));
        const opening=boundaryIndex<0?body:body.slice(0,boundaryIndex);
        const omitted=openingGapOmissions(cue,body);
        const leading=opening.slice(0,first).reduce((sum,block)=>sum+speechExtent(wrapped(block),omitted),0);
        const minimum=leading+speechExtent(opening.slice(first).flatMap(block=>wrapped(block)).slice(0,2),omitted);
        const spoken=body[first],boundary=firstScriptSentenceBoundary(spoken.content);
        if(!scriptSentenceBreaksEnabled(format) || !usesSentenceBreaks(spoken.type,format))return minimum;
        const continues=boundary<spoken.content.length || first<body.length-1;
        const spokenOpening=wrapped(spoken,0,boundary);
        const required=leading+speechExtent(spokenOpening,omitted)+(continues?height(spokenOpening.at(-1)!.style):0);
        return extent(wrapped(cue))+required<=budget?Math.max(minimum,required):minimum;
    }
    function openingBudget(index:number,gap:number):number {
        const previous=blocks[index-1];
        return previous && ['scene-heading','shot'].includes(previous.type) && !styleFor(blocks[index]).startsNewPage
            ?pageFormat.rows-extent(wrapped(previous))-gap:pageFormat.rows;
    }
    function sentenceTake(items:LayoutItem[],cursor:number,take:number):number {
        if(!scriptSentenceBreaksEnabled(format) || take<=0 || cursor+take>=items.length)return take;
        const last=items[cursor+take-1],next=items[cursor+take];
        if(last.block.id!==next.block.id || !usesSentenceBreaks(last.block.type,format))return take;
        let start=cursor+take-1,end=cursor+take;
        while(start>cursor && items[start-1].block.id===last.block.id)start--;
        while(end<items.length && items[end].block.id===last.block.id)end++;
        const sourceStart=items[start].line.sourceStart,fitEnd=last.line.sourceEnd;
        const boundary=lastScriptSentenceBoundary(last.block.content,sourceStart,fitEnd);
        if(boundary===undefined || boundary===fitEnd)return take;
        const prefix=wrapped(last.block,sourceStart,boundary);
        // Keep forward progress for an oversized sentence; never consume more
        // physical rows than the original candidate allowed.
        if(!prefix.length || prefix.length>cursor+take-start)return take;
        const suffix=wrapped(last.block,boundary,items[end-1].line.sourceEnd);
        items.splice(start,end-start,...prefix,...suffix);
        return start-cursor+prefix.length;
    }
    function speechParagraphTake(items:LayoutItem[],cursor:number,take:number,cueHeight=1):number {
        if(!scriptSentenceBreaksEnabled(format) || take<=0 || cursor+take>=items.length)return take;
        const last=items[cursor+take-1],next=items[cursor+take];
        if(last.block.type!=='dialogue' || last.block.id!==next.block.id || !usesSentenceBreaks(last.block.type,format))return take;
        let start=cursor+take-1,end=cursor+take;
        while(start>cursor && items[start-1].block.id===last.block.id)start--;
        while(end<items.length && items[end].block.id===last.block.id)end++;
        if(items[start].line.sourceStart!==0 || lastScriptSentenceBoundary(last.block.content,0,last.line.sourceEnd)!==undefined)return take;
        // A later paragraph has not begun yet. Move its opening with any complete
        // preceding parenthetical, provided the continued page can hold the group.
        let groupStart=start;
        while(groupStart>cursor && items[groupStart-1].block.type==='parenthetical')groupStart--;
        if(groupStart===cursor || items[groupStart].line.sourceStart!==0)return take;
        const boundary=firstScriptSentenceBoundary(last.block.content);
        const continues=boundary<last.block.content.length || end<items.length;
        const required=speechExtent(items.slice(groupStart,start))+speechExtent(wrapped(last.block,0,boundary))+cueHeight+(continues?height(last.style):0);
        // The extra row is the continued cue. Oversized groups retain the fitted
        // split; positive progress here prevents blank or marker-only pages.
        return required<=pageFormat.rows?groupStart-cursor:take;
    }
    let row = initialRow;
    const page = () => pages[pages.length - 1];
    const newPage = () => { if (page().lines.length || pages.length===1 && row>0) pages.push({pageNumber:pages.length + 1,lines:[]}); row = 0; };
    function add(line:WrappedLine, blockId:string, field:string, type:BlockType, style:Style,
        kind:PrintLine['kind'] = 'content', at = row):void {
        page().lines.push({...line,blockId,field,type,kind,width:style.width,align:style.align,
            x:style.align === 'right' ? style.x + style.width : style.align === 'center' ? style.x + style.width / 2 : style.x,
            y:pageFormat.top + at * SCRIPT_PAGE.lineHeight + (style.ascent??SCRIPT_PAGE.fontSize * 0.8),
            ...(style.fontSize&&style.fontSize!==12?{fontSize:style.fontSize}:{}),
            ...(style.ascent&&style.ascent!==9.6?{ascent:style.ascent}:{}),
            ...(style.lineHeight && style.lineHeight!==12?{lineHeight:style.lineHeight}:{})});
    }
    function before(lines:number, gap:number, keep = 0):void {
        const required = Math.min(pageFormat.rows,lines + keep);
        if (row && row + gap + required > pageFormat.rows) newPage();
        if (row) row += gap;
    }
    function plain(block:ScriptBlockData, keep = 0, budget = pageFormat.rows):void {
        const type = block.type === 'dual-dialogue' || !Object.hasOwn(styles,block.type) ? 'action' : block.type;
        const style = styleFor(block);
        const lines = wrapped(block);
        before(keep ? extent(lines) : openingRows(block,budget),style.before,keep);
        let cursor = 0;
        while (cursor < lines.length) {
            if (row+height(lines[cursor].style)>pageFormat.rows) newPage();
            const available = pageFormat.rows - row;
            let take = fit(lines,cursor,available);
            if (lines.length - cursor - take === 1 && take > 1) take--;
            take=sentenceTake(lines,cursor,take);
            for (const item of lines.slice(cursor,cursor + take)) {
                add(item.line,block.id,'content',type,item.style); row+=height(item.style);
            }
            cursor += take;
            if (cursor < lines.length) newPage();
        }
    }
    const marker = (text:string, blockId:string, type:BlockType, style:Style, kind:'more'|'continued') => {
        const fontSize=(style.ascent??9.6)*10/8;
        const cells=Math.floor(style.width/(fontSize*.6)),characters=Array.from(text);
        if(characters.length>cells){warnings.add('A narrow paragraph column shortens a continuation label.');text=characters.slice(0,Math.max(0,cells-1)).join('')+'…';}
        add({text,sourceStart:0,sourceEnd:0},blockId,'',type,{...style,fontSize},kind);
    };
    function continuedCue(cue:string, width:number,fontSize=12):string {
        const name = cue.replace(/\r\n|\r|\n/g,' ').replace(/\s*\(CONT['’]D\)\s*$/i,'');
        const text = `${name} (CONT'D)`;
        const max = Math.floor(width / (fontSize*.6));
        return Array.from(text).length <= max ? text :
            Array.from(name).slice(0,Math.max(0,max - 12)).join('') + "… (CONT'D)";
    }
    function speech(cue:ScriptBlockData, body:ScriptBlockData[],budget:number):void {
        const cueLines=wrapped(cue),cueSize=Math.max(...cueLines.map(l=>l.style.ascent*10/8));
        const cueStyle={...styleFor(cue),fontSize:cueSize,ascent:cueSize*8/10,lineHeight:Math.max(...cueLines.map(l=>l.style.lineHeight))};
        const {openingBreak,breaks}=speechBoundaries(body);
        if(openingBreak)newPage();
        const stream = body.flatMap(block => wrapped(block));
        const omitted=openingGapOmissions(cue,body);
        if(omitted.size)warnings.add('A dialogue paragraph gap that cannot fit with its cue is omitted at the page start.');
        const forcedAt=(index:number)=>stream[index]?.line.sourceStart===0 && breaks.has(stream[index].block.id);
        let suppressFirstGap=false;
        const gapAt=(index:number,start:number)=>index===start&&suppressFirstGap || omitted.has(stream[index].block.id)?0:paragraphGap(stream[index]);
        const fitSpeech=(start:number,budget:number)=>{
            let count=0,used=0;
            while(start+count<stream.length){
                const index=start+count;
                if(count && forcedAt(index))break;
                const size=gapAt(index,start)+height(stream[index].style);
                if(used+size>budget+1e-7)break;
                used+=size;count++;
            }
            return count;
        };
        const moreHeight=Math.max(...stream.filter(item=>item.block.type==='dialogue').map(item=>height(item.style)));
        before(extent(cueLines),cueStyle.before,speechKeep(cue,body,budget));
        for (const item of cueLines) {
            if (row+height(item.style)>pageFormat.rows) newPage();
            add(item.line,cue.id,'content','character',item.style); row+=height(item.style);
        }
        let cursor = 0, spoken = false;
        while (cursor < stream.length) {
            if (row+height(stream[cursor].style)>pageFormat.rows) newPage();
            const available = pageFormat.rows - row;
            const remaining = stream.length - cursor;
            // An oversized authored gap must not strand the cue or generate
            // marker-only pages. Only the unrendered first gap is suppressed.
            if(gapAt(cursor,cursor)+height(stream[cursor].style)>available){
                suppressFirstGap=true;
                warnings.add('A dialogue paragraph gap that cannot fit with its cue is omitted at the page start.');
            }
            const fitted=fitSpeech(cursor,available),overflow=fitted<remaining;
            const withMarker=overflow?fitSpeech(cursor,available-moreHeight):fitted;
            let take=withMarker>0?withMarker:fitted;
            // Avoid a parenthetical alone at the bottom when its complete run can
            // move together. Oversized parentheticals must still make progress.
            if (overflow && take > 1 && stream[cursor + take - 1].block.type === 'parenthetical') {
                let boundary = take;
                while (boundary > 0 && stream[cursor + boundary - 1].block.type === 'parenthetical') boundary--;
                if (boundary > 0) take = boundary;
            }
            take=speechParagraphTake(stream,cursor,take,height(cueStyle));
            take=sentenceTake(stream,cursor,take);
            const segment = stream.slice(cursor,cursor + take);
            segment.forEach(({line,block,style},index) => {
                row+=gapAt(cursor+index,cursor);add(line,block.id,'content',block.type,style);row+=height(style);
            });
            const spokeHere = segment.some(item => item.block.type === 'dialogue');
            spoken ||= spokeHere;
            cursor += take;
            if (cursor < stream.length) {
                const speechRemains = stream.slice(cursor).some(item=>item.block.type==='dialogue');
                const dialogueStyle=[...segment].reverse().find(item=>item.block.type==='dialogue')?.style;
                if (dialogueStyle && speechRemains && row+height(dialogueStyle)<=pageFormat.rows) {
                    marker('(MORE)',cue.id,'dialogue',{...dialogueStyle,align:'center'},'more'); row+=height(dialogueStyle);
                }
                newPage();
                // FD13.4 00dc9e40 preserves paragraph+24c when paragraph+4 has
                // StartsNewPage mask4. Story adopts this on explicit boundaries;
                // ordinary overflow suppresses the new page's first gap.
                suppressFirstGap=!forcedAt(cursor);
                if (spoken && speechRemains) {
                    marker(continuedCue(cue.content,cueStyle.width,cueSize),cue.id,'character',cueStyle,'continued'); row+=height(cueStyle);
                }
            }
        }
    }
    function prepareDual(block:ScriptBlockData) {
        const data=dualFor(block),legacy=!block.dualDialogue;
        const columns=(['left','right'] as const).map(side=>{
            const fields=new Map<string,string>();
            const children=data[side].map((part):ScriptBlockData=>{
                const geometry=scriptDualGeometry(side,part.type);
                const resolved=resolveParagraphFormat(part,format,{...geometry,before:part.type==='character'?1:0});
                resolved.warnings.forEach(w=>warnings.add(w));
                const local=part.richText?.paragraphAttributes??{};
                const inherited=format.elements[part.type==='character'?'Character':part.type==='dialogue'?'Dialogue':'Parenthetical']??{};
                if(['LeftIndent','RightIndent','FirstIndent','Alignment'].some(key=>local[key]!==undefined||inherited[key]!==undefined))
                    warnings.add('Dual dialogue uses Story column geometry; imported column overrides are retained in FDX.');
                fields.set(part.id,legacy?`${side}.${part.type}`:`${side}.${part.id}`);
                // Apply supported resolved attributes to disposable child views.
                // Fixed column geometry is explicit; original rich metadata stays
                // on the document for editing/export and print-run lookup.
                return {...part,richText:{schemaVersion:1,runs:part.richText?.runs??[{text:part.content,attributes:{}}],paragraphAttributes:{
                    ...local,LeftIndent:String(geometry.x/72),RightIndent:String((geometry.x+geometry.width)/72),
                    FirstIndent:'0',Alignment:'Left',SpaceBefore:String(resolved.before*12),
                    Spacing:String(resolved.lineHeight/12),StartsNewPage:resolved.startsNewPage?'Yes':'No',
                }}};
            });
            const firstSpeech=children.findIndex(part=>part.type==='dialogue');
            const openingBreak=children.slice(0,firstSpeech+1).some(part=>part.richText!.paragraphAttributes.StartsNewPage==='Yes');
            // The parent moves both openings once; do not insert a second child
            // page before its original cue. Later authored boundaries remain.
            children.slice(0,firstSpeech+1).forEach(part=>{part.richText!.paragraphAttributes.StartsNewPage='No';});
            return {side,children,fields,openingBreak};
        });
        if(legacy){
            // Legacy four-field blocks historically align both first dialogues
            // below the taller cue. Keep that contract without rewriting JSON.
            const cueHeights=columns.map(c=>{
                const first=c.children[0];
                const laid=layoutScreenplayFlow([first],format,0);
                return laid.pages.flatMap(p=>p.lines).reduce((sum,l)=>sum+(l.lineHeight??12)/12,0);
            });
            columns.forEach((c,i)=>{
                const attrs=c.children[1].richText!.paragraphAttributes;
                attrs.SpaceBefore=String(Number(attrs.SpaceBefore)+(Math.max(...cueHeights)-cueHeights[i])*12);
            });
        }
        columns.forEach(column=>{
            const attrs=column.children[0].richText!.paragraphAttributes,gap=Number(attrs.SpaceBefore)/12;
            if(!gap)return;
            const opening=layoutScreenplayFlow(column.children,format,0).pages[0].lines.filter(l=>l.kind==='content');
            const first=opening.findIndex(l=>l.type==='dialogue');
            const required=first<0?pageFormat.rows:Math.max(...opening.slice(0,first+2).map(l=>(l.y-pageFormat.top-(l.ascent??9.6)+(l.lineHeight??12))/12));
            if(gap+required>pageFormat.rows){
                attrs.SpaceBefore='0';
                warnings.add('A dual-dialogue cue gap that cannot fit with its opening is omitted.');
            }
        });
        return {columns,openingBreak:columns.some(c=>c.openingBreak)};
    }
    type DualPlan=ReturnType<typeof prepareDual>;
    const dualPlans=new Map<string,DualPlan>();
    function dualPlan(block:ScriptBlockData):DualPlan {
        let plan=dualPlans.get(block.id);if(!plan){plan=prepareDual(block);dualPlans.set(block.id,plan);}return plan;
    }
    function dual(block:ScriptBlockData):void {
        let plan:DualPlan;
        try {plan=dualPlan(block);}catch{issues.push('A dual-dialogue block is malformed. Repair it before exporting.');plain(block);return;}
        if(plan.openingBreak)newPage();
        const run=(start:number)=>plan.columns.map(column=>({...column,layout:layoutScreenplayFlow(column.children,format,start)}));
        let columns=run(row);
        // Keep the paired opening together if either child cannot start here.
        if(row && columns.some(c=>!c.layout.pages[0].lines.some(l=>l.kind==='content'))){newPage();columns=run(0);}
        columns.forEach(c=>{c.layout.warnings?.forEach(w=>warnings.add(w));issues.push(...c.layout.issues);});
        const count=Math.max(...columns.map(c=>c.layout.pages.length));
        for(let index=0;index<count;index++){
            if(index)newPage();
            const merged=columns.flatMap(column=>(column.layout.pages[index]?.lines??[]).map(line=>({
                ...line,blockId:block.id,field:line.kind==='content'?column.fields.get(line.blockId)!:'',
            }))).sort((a,b)=>a.y-b.y||a.x-b.x);
            page().lines.push(...merged);
            row=Math.max(0,...merged.map(line=>(line.y-pageFormat.top-(line.ascent??9.6))/12+(line.lineHeight??12)/12));
        }
    }
    function dualOpeningHeight(block:ScriptBlockData,budget:number):number {
        try{
            const plan=dualPlan(block);if(plan.openingBreak)return 0;
            return Math.max(...plan.columns.map(column=>{
                const gap=Number(column.children[0].richText!.paragraphAttributes.SpaceBefore)/12;
                const layout=layoutScreenplayFlow(column.children,format,0);
                const content=layout.pages[0].lines.filter(l=>l.kind==='content');
                const first=content.findIndex(l=>l.type==='dialogue');
                if(first<0)return pageFormat.rows;
                const occupied=(line:PrintLine)=>(line.y-pageFormat.top-(line.ascent??9.6)+(line.lineHeight??12))/12;
                const minimum=Math.max(...content.slice(0,first+2).map(occupied));
                const part=column.children.find(p=>p.id===content[first].blockId)!;
                if(!scriptSentenceBreaksEnabled(format)||!usesSentenceBreaks(part.type,format))return gap+minimum;
                const boundary=firstScriptSentenceBoundary(part.content);
                const sentence=content.filter(l=>l.blockId===part.id&&l.sourceStart<boundary);
                if(!sentence.length||sentence.at(-1)!.sourceEnd<boundary)return gap+minimum;
                const required=occupied(sentence.at(-1)!)+(boundary<part.content.length?(sentence.at(-1)!.lineHeight??12)/12:0);
                return gap+(gap+required<=budget?Math.max(minimum,required):minimum);
            }));
        }catch{return 4;}
    }

    for (let index = 0; index < blocks.length; index++) {
        const block = blocks[index];
        if(styleFor(block).startsNewPage)newPage();
        if (block.type === 'dual-dialogue') { dual(block); continue; }
        if (!Object.hasOwn(styles,block.type)) issues.push('A script element has an unknown type. Repair it before exporting.');
        if (block.type === 'character') {
            const body:ScriptBlockData[] = [];
            let next = index + 1;
            while (next < blocks.length && ['parenthetical','dialogue'].includes(blocks[next].type)) body.push(blocks[next++]);
            if (body.some(item => item.type === 'dialogue')) { speech(block,body,openingBudget(index,styleFor(block).before)); index = next - 1; continue; }
        }
        let keep = 0;
        if (['scene-heading','shot'].includes(block.type) && blocks[index + 1] && !styleFor(blocks[index+1]).startsNewPage) {
            const next = blocks[index + 1];
            if (next.type === 'character') {
                const body:ScriptBlockData[]=[];
                let cursor = index + 2;
                while (blocks[cursor] && ['parenthetical','dialogue'].includes(blocks[cursor].type)) {
                    body.push(blocks[cursor++]);
                }
                if(!body.some(part=>part.type==='dialogue') || !speechBoundaries(body).openingBreak){
                    keep=styleFor(next).before+extent(wrapped(next));
                    if(body.some(part=>part.type==='dialogue'))keep+=speechKeep(next,body,pageFormat.rows-extent(wrapped(block))-styleFor(next).before);
                }
            } else if (next.type === 'dual-dialogue') keep = dualOpeningHeight(next,pageFormat.rows-extent(wrapped(block)));
            else {
                keep = styleFor(next).before + openingRows(next,pageFormat.rows-extent(wrapped(block))-styleFor(next).before);
            }
        }
        plain(block,keep,openingBudget(index,styleFor(block).before));
    }
    for (const candidate of pages.flatMap(page => page.lines)) {
        if (hasUnsupportedPDFText(candidate.text)) { issues.push('PDF export cannot render some characters with the current font. The original text is preserved; a font with broader character support is needed.'); break; }
    }
    return {pages,issues,...(warnings.size?{warnings:[...warnings]}:{})};
}

export function layoutTitlePage(data:TitlePageData):PrintPage {
    const lines:PrintLine[] = [];
    let row = 21;
    function add(text:string, start:number, width:number, x:number, align:PrintLine['align']):number {
        const wrapped = wrapScriptText(text,width);
        wrapped.forEach((line,index) => lines.push({...line,blockId:'title-page',field:'',type:'action',kind:'title',
            x,y:(start + index) * 12 + 9.6,width,align}));
        return start + wrapped.length;
    }
    row = add((data.title || 'UNTITLED SCREENPLAY').toUpperCase(),row,432,306,'center');
    row = add('Written by',row + 2,432,306,'center');
    row = add(data.author || 'Unknown Author',row + 1,432,306,'center');
    if (data.basedOn) {
        row = add('Based on',row + 2,432,306,'center');
        row = add(data.basedOn,row + 1,432,306,'center');
    }
    const contactRows = data.contact ? wrapScriptText(data.contact,250).length : 0;
    const contactStart = 60 - contactRows;
    if (row + 2 > contactStart || contactStart < 6) throw new Error('Title-page text is too long to fit. Shorten the title, credits, or contact details before exporting.');
    if (data.contact) add(data.contact,contactStart,250,72,'left');
    return {kind:'title',lines};
}

export function layoutPrintDocument(layout:ScriptLayout, title:TitlePageData):PrintPage[] {
    const pages:PrintPage[] = title.visible === false ? [] : [layoutTitlePage(title)];
    pages.push(...layout.pages.map(page => ({kind:'script' as const,pageNumber:page.pageNumber,lines:[
        ...(page.pageNumber > 1 ? [{text:`${page.pageNumber}.`,sourceStart:0,sourceEnd:0,blockId:'',field:'',
            type:'action' as const,kind:'page-number' as const,x:540,y:36,width:36,align:'right' as const}] : []),...page.lines]})));
    return pages;
}
